import { defineStore } from 'pinia';
import { ref } from 'vue';
import {
  exposeSettingsListState,
  createSettingsMutationState,
  createSettingsReadState,
} from './settings-list-state.factory';
import type { SettingsListQuery } from '../lib/settings-list-query';
import type {
  SettingsListItem,
  SettingsListItemCreated,
  SettingsListItemUpdated,
} from './settings.types';

export interface SettingsListApi<T extends SettingsListItem> {
  list(query?: SettingsListQuery): Promise<T[]>;
  create(names: string[]): Promise<SettingsListItemCreated[]>;
  update(id: number, name: string): Promise<SettingsListItemUpdated<T> | null>;
  remove(id: number): Promise<boolean>;
}

export function createSettingsListStore<T extends SettingsListItem>(
  storeId: string,
  api: SettingsListApi<T>
) {
  return defineStore(storeId, () => {
    const read = createSettingsReadState<T>((query) => api.list(query), 'Failed to fetch items');
    const mutation = createSettingsMutationState();
    const isCreating = ref(false);
    const isUpdating = ref(false);
    const isDeleting = ref(false);

    async function create(names: string[]): Promise<boolean> {
      try {
        isCreating.value = true;
        mutation.reset();
        const created = await api.create(names);
        if (created.length === 0) return false;
      } catch (error: unknown) {
        mutation.record(error, 'Failed to create items');
        return false;
      } finally {
        isCreating.value = false;
      }

      // The created rows come back without createdAt, which the listing orders by.
      await read.fetchAll();
      return true;
    }

    async function update(id: number, name: string): Promise<boolean> {
      try {
        isUpdating.value = true;
        mutation.reset();
        const updated = await api.update(id, name);
        if (!updated) return false;
      } catch (error: unknown) {
        mutation.record(error, 'Failed to update item');
        return false;
      } finally {
        isUpdating.value = false;
      }

      // The renamed row may no longer belong to the query in force, and the listing decides that,
      // not the answer to the write. The row is saved either way, so a failure here is the failure
      // of the read that followed.
      await read.fetchAll();
      return true;
    }

    async function remove(id: number): Promise<boolean> {
      try {
        isDeleting.value = true;
        mutation.reset();
        const removed = await api.remove(id);
        if (removed) read.items.value = read.items.value.filter((item) => item.id !== id);
        return removed;
      } catch (error: unknown) {
        mutation.record(error, 'Failed to delete item');
        return false;
      } finally {
        isDeleting.value = false;
      }
    }

    return {
      ...exposeSettingsListState(read, mutation, { isCreating, isUpdating, isDeleting }),
      create,
      update,
      remove,
    };
  });
}
